import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Granel Vital — fragmento de producto reactivo a la variante seleccionada.
 *
 * Envuelve cualquier dato derivado de la variante (precio por gramo, gramaje,
 * rendimiento en dosis, badge de estandarización) y lo re-sincroniza cuando el
 * cliente cambia de presentación.
 *
 * Reutiliza el HTML que el tema ya solicitó para el cambio de variante
 * (`detail.html`), igual que `product-price.js`: no hace peticiones propias,
 * no toca el carrito y no interfiere con el tracking.
 *
 * Uso:
 *   <gv-product-fragment data-block-id="{{ block.id }}" data-product-id="{{ product.id }}">
 *     …markup dependiente de la variante…
 *   </gv-product-fragment>
 *
 * @extends {Component}
 */
class GvProductFragment extends Component {
  connectedCallback() {
    super.connectedCallback();

    const scope = this.closest('.shopify-section, dialog');
    if (!scope) return;

    this.#scope = scope;
    scope.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#scope = null;
  }

  /** @type {Element | null} */
  #scope = null;

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Un cambio de variante dentro de una card no debe reescribir la PDP.
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card') && !this.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector(`gv-product-fragment[data-block-id="${this.dataset.blockId}"]`);
        if (!next) return;

        this.innerHTML = next.innerHTML;
        this.toggleAttribute('hidden', next.hasAttribute('hidden'));
      })
      .catch(() => {
        // El tema ya reporta el error de la petición de variante; aquí basta
        // con conservar el último valor renderizado en servidor.
      });
  };
}

if (!customElements.get('gv-product-fragment')) {
  customElements.define('gv-product-fragment', GvProductFragment);
}
